/**
 * Canonical speaker-initials formatter — the single implementation that unifies
 * the four hand-rolled forks that existed across the app:
 *
 *   - `MeetingSummary/helpers.ts` `initialsOf`   (first-two words → up to 2 letters)
 *   - `AvatarStack/helpers.ts` `getInitials`     (first-two words → up to 2 letters)
 *   - `RecordingSurface/TranscriptCard` `speakerInitial` (raw label → "S{n}")
 *   - `SpeakerCard/SpeakerCardView` `speaker[0]` (first letter only)
 *
 * Behaviour:
 *   - Strips the far-end `Remote::` identity prefix first (the initials describe
 *     the person, never the channel).
 *   - Raw diarisation labels ("Speaker_S1", "Speaker_1", "Speaker 1",
 *     "SPEAKER_03") collapse to the short "S{n}" form the transcript uses, with
 *     leading zeros dropped ("SPEAKER_03" → "S3"). The S-prefixed shape the old
 *     TranscriptCard regex missed is handled here too, so "Speaker_S1" → "S1".
 *   - Any other name yields up to two uppercase initials taken from its FIRST
 *     and LAST word ("Brother Velmar" → "BV", "Mary Jane Watson" → "MW",
 *     "Aelora" → "A").
 *   - Empty / blank input → "?".
 */

const REMOTE_PREFIX = "Remote::"

/** Grapheme-safe first character of a word (handles surrogate pairs). */
function firstChar(word: string): string {
  return Array.from(word)[0] ?? ""
}

export function speakerInitials(name: string, displayIndex?: number): string {
  const core = name.startsWith(REMOTE_PREFIX) ? name.slice(REMOTE_PREFIX.length) : name
  const trimmed = core.trim()
  if (!trimmed) return "?"

  // Raw diarisation label → short "S{n}" form. `s?` also matches the S-prefixed
  // shape ("Speaker_S1"); `0*` drops leading zeros ("SPEAKER_03" → "S3").
  const rawLabel = /^speaker[_\s-]*s?0*(\d+)$/i.exec(trimmed)
  if (rawLabel) {
    // Prefer the unified display number so an avatar can never disagree with the
    // "Speaker N" name the chip renders next to it (a raw label's own number is a
    // per-channel index, not the meeting-wide one). Fall back to the label's own
    // number when no unified index is known (synthetic / direct-label callers).
    const unified = displayIndex !== undefined && displayIndex > 0 ? displayIndex : Number(rawLabel[1])
    return `S${unified}`
  }

  const words = trimmed.split(/\s+/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return firstChar(words[0]).toUpperCase()
  const first = firstChar(words[0])
  const last = firstChar(words[words.length - 1])
  return (first + last).toUpperCase()
}
